import {
  implementationTypologies,
  STORE_AGE_BRACKETS,
  type StoreAgeBracket,
} from '@gozoki/api-types'
import { z } from 'zod'
import {
  COMPARED_STORE_SETS,
  COMPARISON_PERIODS,
  type ComparisonPeriodChoice,
} from '~/components/dashboards/filter-bar/comparison-model'
import {
  LEGACY_WEEK_PRESETS,
  PERIOD_PRESETS,
} from '~/components/dashboards/filter-bar/period-model'
import { realIsoDateSchema } from '~/lib/iso-date'

const LEGACY_OPENING_AGES: Record<string, StoreAgeBracket[]> = {
  comparables: ['more_than_12_months'],
  new: ['less_than_3_months', 'between_3_and_6_months', 'between_6_and_12_months'],
}

const LEGACY_COMPARISON_PERIODS: Record<string, ComparisonPeriodChoice> = {
  previous: 'previous',
  last_year: 'year_before',
}

export const networkDashboardSearchSchema = z
  .object({
    storeIds: z.array(z.number()).default([]).catch([]),
    pickedStoreIds: z.array(z.number()).default([]).catch([]),
    noStore: z.literal(true).optional().catch(undefined),
    ages: z
      .array(z.string())
      .default([])
      .catch([])
      .transform((codes) => STORE_AGE_BRACKETS.filter((bracket) => codes.includes(bracket))),
    opening: z.string().optional().catch(undefined),
    typologies: z
      .array(z.string())
      .default([])
      .catch([])
      .transform((codes) => implementationTypologies.filter((code) => codes.includes(code))),
    periodPreset: z.enum(PERIOD_PRESETS).optional().catch(undefined),
    weekPreset: z.enum(LEGACY_WEEK_PRESETS).optional().catch(undefined),
    startDate: realIsoDateSchema.optional().catch(undefined),
    endDate: realIsoDateSchema.optional().catch(undefined),
    comparison: z.enum(['on', 'none', 'previous', 'last_year']).default('on').catch('on'),
    compPreset: z.enum(COMPARISON_PERIODS).optional().catch(undefined),
    compStartDate: realIsoDateSchema.optional().catch(undefined),
    compEndDate: realIsoDateSchema.optional().catch(undefined),
    compStores: z.enum(COMPARED_STORE_SETS).default('same').catch('same'),
    compStoreIds: z.array(z.number()).default([]).catch([]),
  })
  .transform(({ opening, comparison, compPreset, ...search }) => ({
    ...search,
    ages:
      search.ages.length > 0 || opening === undefined
        ? search.ages
        : (LEGACY_OPENING_AGES[opening] ?? []),
    comparison: comparison === 'none' ? ('none' as const) : ('on' as const),
    compPreset: compPreset ?? LEGACY_COMPARISON_PERIODS[comparison] ?? 'previous',
  }))

export type NetworkDashboardSearch = z.infer<typeof networkDashboardSearchSchema>
